import type {Direction, Style, StyleRef} from '@wix/document-services-types'
import type {LogicalTextAlign, PhysicalTextAlign, StyleProps} from './types'

const extractUnit = (valueWithUnit: string) => {
    const DECIMALS_REGEX = /-?\d+(\.\d+)?/
    const FALLBACK_UNIT = 'px'
    return valueWithUnit.replace(DECIMALS_REGEX, '') || FALLBACK_UNIT
}

export const parseValueWithUnit = (valueWithUnit: string) => {
    const numericValue = parseFloat(valueWithUnit)
    const unit = extractUnit(valueWithUnit)

    return {
        value: numericValue,
        unit: unit || 'px'
    }
}

export const computeAlign = (
    menuDirection: string | undefined,
    align: string | undefined,
    itemDirection?: string | undefined
) => {
    if (!align || !menuDirection) {
        return undefined
    }

    const alignmentWithoutFlexPrefix = align.replace('flex-', '') as 'start' | 'center' | 'end'

    if (menuDirection === 'ltr' || alignmentWithoutFlexPrefix === 'center') {
        return alignmentWithoutFlexPrefix
    }

    if (itemDirection && menuDirection === itemDirection) {
        return alignmentWithoutFlexPrefix
    }

    return alignmentWithoutFlexPrefix === 'start' ? 'end' : 'start'
}

const logicalToPhysicalAlignMapper: Record<Direction, Record<LogicalTextAlign, PhysicalTextAlign>> = {
    ltr: {
        start: 'left',
        end: 'right',
        center: 'center'
    },
    rtl: {
        start: 'right',
        end: 'left',
        center: 'center'
    }
}

export const getPhysicalItemTextAlignFromLogical = (styleItem: StyleRef): PhysicalTextAlign => {
    const key = 'item-text-align'
    const direction: Direction = styleItem.style.properties['menu-direction'] ?? 'ltr'
    const justifyContent: LogicalTextAlign = styleItem.style.properties['item-justify-content'] ?? 'center'
    const textAlighnment: LogicalTextAlign = styleItem.style.properties[key] ?? justifyContent
    return logicalToPhysicalAlignMapper[direction][textAlighnment]
}

const clearTemporaryPropertiesByPrefix = (prefix: string, styleProps: StyleProps) => {
    Object.keys(styleProps).forEach(key => {
        if (key.startsWith(prefix)) {
            delete styleProps[key]
        }
    })
}

const MENU_TOP_LEVEL_TEMPORARY_PROPERTIES = [
    'container-flex-grow',
    'container-justify-content',
    'item-padding-top',
    'item-padding-right',
    'item-justify-content',
    'menu-direction',
    'item-direction',
    'menu-flex-grow',
    'menu-height',
    'menu-margin-left',
    'menu-margin-right',
    'menu-margin-top',
    'menu-margin-bottom',
    'scroll-button-padding-top',
    'scroll-button-hover-padding-top',
    'scroll-button-padding-bottom',
    'scroll-button-hover-padding-bottom'
]

const clearMenuTopLevelTemporaryProperties = (properties: StyleProps) => {
    MENU_TOP_LEVEL_TEMPORARY_PROPERTIES.forEach(property => {
        delete properties[property]
    })
}

const DROPDOWN_TEMPORARY_PROPERTIES = [
    'dropdown-container---flyoutAlignment',
    'dropdown-container---horizontalSpacing',
    'dropdown-menu-justify-content',
    'dropdown-margin-right',
    'dropdown-menu---width',
    'dropdown-menu-width',
    'dropdown-menu---columnsAmount',
    'dropdown-menu-text-align',
    'dropdown-menu-item-direction',
    'dropdown-menu-sub-item-direction'
]

const clearDropdownTemporaryProperties = (properties: StyleProps) => {
    DROPDOWN_TEMPORARY_PROPERTIES.forEach(property => {
        delete properties[property]
    })
}

const clearMegaMenuTemporaryProperties = (properties: StyleProps) => {
    clearTemporaryPropertiesByPrefix('mega-menu-', properties)
}

/**
 * @param {object} properties
 * */
const clearTemporaryProperties = (properties: StyleProps) => {
    clearMenuTopLevelTemporaryProperties(properties)
    clearDropdownTemporaryProperties(properties)
    clearMegaMenuTemporaryProperties(properties)
}

export const clearAllTemporaryProperties = ({
    styleProperties,
    propertiesOverride,
    propertiesSource
}: {
    styleProperties: Style['properties']
    propertiesOverride?: Style['properties']
    propertiesSource: Style['properties']
}) => {
    clearTemporaryProperties(styleProperties)
    if (propertiesOverride) {
        clearTemporaryProperties(propertiesOverride)
    }
    clearTemporaryProperties(propertiesSource)
}

export const migratePropertyBetweenStyleItems = ({
    fromKey,
    toKey,
    from,
    to,
    sourceValue = 'value'
}: {
    fromKey: string
    toKey: string
    from: StyleRef
    to: StyleRef
    sourceValue?: string
}) => {
    if (!from.style.properties[fromKey]) return

    to.style.properties[toKey] = from.style.properties[fromKey]
    to.style.propertiesSource[toKey] = sourceValue
}

export const migratePropertyWithinStyleItem = ({
    styleItem,
    fromKey,
    toKey,
    fallback
}: {
    styleItem: StyleRef
    fromKey: string
    toKey: string
    fallback?: string
}) => {
    if (!styleItem.style.properties[fromKey] && !fallback) return

    styleItem.style.properties[toKey] = styleItem.style.properties[fromKey] ?? fallback
    styleItem.style.propertiesSource[toKey] = 'value'

    delete styleItem.style.properties[fromKey]
    delete styleItem.style.propertiesSource[fromKey]
}

export const setProperty = ({
    key,
    value,
    styleItem,
    sourceValue = 'value'
}: {
    key: string
    value: string | undefined
    styleItem: StyleRef
    sourceValue?: string
}) => {
    if (!value) {
        return
    }

    styleItem.style.properties[key] = value
    styleItem.style.propertiesSource[key] = sourceValue
}

export const deleteProperty = ({key, styleItem}: {key: string; styleItem: StyleRef}) => {
    delete styleItem.style.properties[key]
    delete styleItem.style.propertiesSource[key]
}
